import { FormEvent, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { CheckCircle, EnvelopeSimple, Sparkle, WarningCircle } from '@phosphor-icons/react'
import { toast } from 'sonner'
import { Container } from '@/components/layout/Container'
import { Card } from '@/components/primitives/Card'
import { PrimaryButton } from '@/components/primitives/PrimaryButton'
import { Input } from '@/components/ui/input'
import { hotmartApi, type HotmartClaimView } from '@/lib/hotmart-api'
import { api } from '@/lib/api'

const emptyClaim: HotmartClaimView = { valid: false }

export function HotmartWelcomePage() {
  const { locale, token } = useParams<{ locale: string; token?: string }>()
  const resolvedLocale = locale || 'en'
  const isPortuguese = resolvedLocale === 'pt'

  const copy = useMemo(() => ({
    title: isPortuguese ? 'Ative seu acesso PinDay' : 'Activate your PinDay access',
    subtitle: isPortuguese
      ? 'Comprou pela Hotmart? Finalize seu acesso aqui sem depender do suporte.'
      : 'Purchased through Hotmart? Finish your account setup here.',
    resendTitle: isPortuguese ? 'Reenviar link de acesso' : 'Resend access link',
    resendDescription: isPortuguese
      ? 'Informe o mesmo e-mail usado na Hotmart e enviaremos um novo link se houver uma ativação pendente.'
      : 'Use the same Hotmart purchase email and we will resend the activation link if one is pending.',
    emailLabel: isPortuguese ? 'E-mail da compra' : 'Purchase email',
    nameLabel: isPortuguese ? 'Seu nome' : 'Your name',
    businessLabel: isPortuguese ? 'Nome do negócio' : 'Business name',
    passwordLabel: isPortuguese ? 'Senha' : 'Password',
    activate: isPortuguese ? 'Ativar conta' : 'Activate account',
    resend: isPortuguese ? 'Enviar novo link' : 'Send new link',
    existingAccount: isPortuguese
      ? 'Encontramos uma conta PinDay com esse mesmo e-mail. Vamos anexar sua compra a ela.'
      : 'We found an existing PinDay account with this same email. We will attach your purchase to it.',
    existingPasswordHint: isPortuguese
      ? 'Se essa conta ainda não tiver senha, defina uma agora.'
      : 'If that account does not have a password yet, set one now.',
    invalidToken: isPortuguese
      ? 'Esse link expirou ou já foi usado.'
      : 'This link has expired or has already been used.',
    successResend: isPortuguese
      ? 'Se encontramos uma ativação pendente, enviamos um novo link.'
      : 'If we found a pending activation, we sent a new link.',
    claimSummary: isPortuguese ? 'Resumo da compra' : 'Purchase summary',
    continueText: isPortuguese
      ? 'Depois de concluir, você entra direto no PinDay com o plano correto.'
      : 'After this, you will be logged into PinDay with the correct plan.',
    login: isPortuguese ? 'Ir para login' : 'Go to login',
    sending: isPortuguese ? 'Enviando...' : 'Sending...',
    activating: isPortuguese ? 'Ativando...' : 'Activating...',
    genericError: isPortuguese ? 'Não foi possível concluir sua ativação.' : 'Could not complete your activation.',
    fillRequired: isPortuguese
      ? 'Preencha nome, negócio e uma senha com 6+ caracteres.'
      : 'Fill name, business name, and a password with at least 6 characters.',
  }), [isPortuguese])

  const [claim, setClaim] = useState<HotmartClaimView>(emptyClaim)
  const [email, setEmail] = useState('')
  const [name, setName] = useState('')
  const [businessName, setBusinessName] = useState('')
  const [password, setPassword] = useState('')
  const [loadingClaim, setLoadingClaim] = useState(Boolean(token))
  const [submitting, setSubmitting] = useState(false)
  const [resending, setResending] = useState(false)

  useEffect(() => {
    let active = true

    const loadClaim = async () => {
      if (!token) {
        setClaim(emptyClaim)
        setLoadingClaim(false)
        return
      }

      setLoadingClaim(true)
      try {
        const response = await hotmartApi.getClaim(token)
        if (!active) return
        setClaim(response)
        if (response.email) {
          setEmail(response.email)
        }
      } catch (error) {
        if (!active) return
        console.error('Failed to load Hotmart claim:', error)
        setClaim(emptyClaim)
      } finally {
        if (active) {
          setLoadingClaim(false)
        }
      }
    }

    loadClaim()

    return () => {
      active = false
    }
  }, [token])

  const handleResend = async (event: FormEvent) => {
    event.preventDefault()
    if (!email.trim()) {
      toast.error(copy.emailLabel)
      return
    }

    setResending(true)
    try {
      await hotmartApi.resendClaim(email.trim())
      toast.success(copy.successResend)
    } catch (error) {
      console.error('Failed to resend Hotmart claim:', error)
      toast.error(copy.genericError)
    } finally {
      setResending(false)
    }
  }

  const handleComplete = async (event: FormEvent) => {
    event.preventDefault()
    if (!token) return

    if (!claim.hasExistingUser) {
      if (!name.trim() || !businessName.trim() || password.trim().length < 6) {
        toast.error(copy.fillRequired)
        return
      }
    }

    setSubmitting(true)
    try {
      const response = await hotmartApi.completeClaim(token, {
        ...(claim.hasExistingUser ? {} : { name: name.trim(), businessName: businessName.trim() }),
        ...(password.trim() ? { password: password.trim() } : {}),
        locale: resolvedLocale === 'pt' ? 'pt-BR' : resolvedLocale,
      })

      api.setAuthToken(response.token)
      api.refreshCsrfToken()
      localStorage.setItem('user_data', JSON.stringify({
        id: response.user.id,
        email: response.user.email,
        name: response.user.name,
        tenantId: response.tenant.id,
        role: response.user.role,
        locale: response.user.locale,
        avatarUrl: `https://api.dicebear.com/7.x/avataaars/svg?seed=${response.user.email}`,
      }))

      window.location.assign(`/${resolvedLocale}/dashboard`)
    } catch (error) {
      console.error('Failed to complete Hotmart claim:', error)
      toast.error(copy.genericError)
    } finally {
      setSubmitting(false)
    }
  }

  const showClaimForm = Boolean(token) && claim.valid

  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top,rgba(240,138,36,0.15),transparent_42%),linear-gradient(180deg,#fffaf5,rgba(255,255,255,0.96))]">
      <Container className="py-12">
        <div className="mx-auto max-w-3xl space-y-6">
          <div className="text-center">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-[#0B2A4A] text-white shadow-lg">
              <Sparkle size={26} weight="fill" />
            </div>
            <h1 className="text-4xl font-bold text-[var(--pd-foreground)]">{copy.title}</h1>
            <p className="mt-3 text-[var(--pd-foreground)]/70">{copy.subtitle}</p>
          </div>

          <Card className="overflow-hidden">
            <div className="grid gap-0 md:grid-cols-[1.1fr,0.9fr]">
              <div className="border-b border-[var(--pd-border)] bg-white p-8 md:border-b-0 md:border-r">
                {loadingClaim ? (
                  <div className="flex min-h-[260px] items-center justify-center">
                    <div className="h-8 w-8 animate-spin rounded-full border-4 border-[#0B2A4A] border-t-transparent" />
                  </div>
                ) : showClaimForm ? (
                  <form className="space-y-5" onSubmit={handleComplete}>
                    <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900">
                      <div className="mb-2 flex items-center gap-2 font-semibold">
                        <CheckCircle size={18} weight="fill" />
                        {copy.claimSummary}
                      </div>
                      <p>{claim.email}</p>
                      <p>{claim.subscriptionPlanId?.toUpperCase() || 'PRO'}</p>
                      <p>{copy.continueText}</p>
                    </div>

                    {claim.hasExistingUser ? (
                      <div className="rounded-2xl border border-[var(--pd-border)] bg-[var(--pd-muted)]/40 p-4 text-sm text-[var(--pd-foreground)]/80">
                        <p className="font-medium text-[var(--pd-foreground)]">{copy.existingAccount}</p>
                        <p className="mt-1">{copy.existingPasswordHint}</p>
                      </div>
                    ) : (
                      <>
                        <div className="space-y-2">
                          <label className="text-sm font-medium text-[var(--pd-foreground)]">{copy.nameLabel}</label>
                          <Input value={name} onChange={(event) => setName(event.target.value)} placeholder={copy.nameLabel} />
                        </div>
                        <div className="space-y-2">
                          <label className="text-sm font-medium text-[var(--pd-foreground)]">{copy.businessLabel}</label>
                          <Input value={businessName} onChange={(event) => setBusinessName(event.target.value)} placeholder={copy.businessLabel} />
                        </div>
                      </>
                    )}

                    <div className="space-y-2">
                      <label className="text-sm font-medium text-[var(--pd-foreground)]">{copy.passwordLabel}</label>
                      <Input
                        type="password"
                        value={password}
                        onChange={(event) => setPassword(event.target.value)}
                        placeholder={copy.passwordLabel}
                      />
                    </div>

                    <PrimaryButton type="submit" variant="accent" size="lg" disabled={submitting} fullWidth>
                      {submitting ? copy.activating : copy.activate}
                    </PrimaryButton>
                  </form>
                ) : (
                  <div className="space-y-5">
                    {token ? (
                      <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-red-900">
                        <div className="mb-2 flex items-center gap-2 font-semibold">
                          <WarningCircle size={18} weight="fill" />
                          {copy.invalidToken}
                        </div>
                        <p>{copy.resendDescription}</p>
                      </div>
                    ) : (
                      <div className="rounded-2xl border border-[var(--pd-border)] bg-[var(--pd-muted)]/40 p-4 text-sm text-[var(--pd-foreground)]/80">
                        <p>{copy.resendDescription}</p>
                      </div>
                    )}

                    <form className="space-y-4" onSubmit={handleResend}>
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-[var(--pd-foreground)]">{copy.emailLabel}</label>
                        <Input
                          type="email"
                          value={email}
                          onChange={(event) => setEmail(event.target.value)}
                          placeholder="voce@exemplo.com"
                        />
                      </div>

                      <PrimaryButton type="submit" variant="primary" size="lg" disabled={resending} fullWidth>
                        {resending ? copy.sending : copy.resend}
                      </PrimaryButton>
                    </form>
                  </div>
                )}
              </div>

              <div className="bg-[#0B2A4A] p-8 text-white">
                <div className="mb-5 flex items-center gap-3">
                  <EnvelopeSimple size={22} weight="duotone" />
                  <h2 className="text-xl font-semibold">{copy.resendTitle}</h2>
                </div>
                <div className="space-y-4 text-sm text-white/80">
                  <p>{copy.resendDescription}</p>
                  <p>
                    {isPortuguese
                      ? 'Se você já tem uma conta gratuita com o mesmo e-mail da Hotmart, vamos anexar essa compra ao seu workspace existente.'
                      : 'If you already have a free workspace with the same Hotmart email, we will connect this purchase to that account.'}
                  </p>
                  <p>
                    {isPortuguese
                      ? 'Se nada aparecer, o suporte consegue reenviar ou revisar manualmente o vínculo.'
                      : 'If nothing shows up, support can resend the claim or review the match manually.'}
                  </p>
                  <PrimaryButton
                    variant="outline"
                    className="border-white/20 bg-transparent text-white hover:bg-white/10"
                    onClick={() => {
                      window.location.assign(`/${resolvedLocale}/login`)
                    }}
                  >
                    {copy.login}
                  </PrimaryButton>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </Container>
    </div>
  )
}
